import { type MouseEvent, type KeyboardEvent } from 'react';
import {
    trackEvent,
    trackEcommerce,
    eventNameConstants,
    interactionTypeConstants,
    stepInteractionConstants,
    type EventNameValues,
    getUserEventItem,
    trackUserEventAddToCart,
} from 'dibs-tracking';
import { type Environment, graphql, readInlineData } from 'react-relay';
import serverVars from 'server-vars';
import { type Maybe } from 'dibs-ts-utils/exports/Maybe.ts';
import { type ValueOf } from 'dibs-ts-utils/exports/ValueOf.ts';
import { hasKey } from 'dibs-ts-utils/exports/hasKey.ts';

import { type actionButtonsTracking_item$key } from './__generated__/actionButtonsTracking_item.graphql.ts';

type TrackableEvent = Maybe<KeyboardEvent | MouseEvent>;

type PdpTrackingVpvParams = {
    purchaseStatus?: Maybe<string>;
    priceType?: Maybe<string>;
};

export type PdpTrackingVpvProps = { pdpTrackingVpvParams?: Maybe<PdpTrackingVpvParams> };

type Item = actionButtonsTracking_item$key & PdpTrackingVpvProps;

function isPdpTrackingVpvParams(
    params: unknown,
    key: keyof PdpTrackingVpvParams
): params is PdpTrackingVpvParams {
    return hasKey(params, key);
}

const { EVENT_ADD_CART, EVENT_PRODUCT_INTERACTION } = eventNameConstants;
const {
    INTERACTION_TYPE_MAKE_OFFER,
    INTERACTION_TYPE_ADD_TO_CART,
    INTERACTION_TYPE_ACCEPT_OFFER,
    INTERACTION_TYPE_COUNTER_OFFER,
} = interactionTypeConstants;
const {
    STEP_INTERACTION_MAKE_OFFER,
    STEP_INTERACTION_OFFER_INQUIRY_ENTRY,
    STEP_INTERACTION_ADD_TO_CART,
    STEP_INTERACTION_ACCEPT_OFFER,
    STEP_INTERACTION_COUNTER_OFFER,
} = stepInteractionConstants;

function getPdpTrackingVpvParamValue(
    params: unknown,
    key: keyof PdpTrackingVpvParams
): Maybe<ValueOf<PdpTrackingVpvParams>> {
    return isPdpTrackingVpvParams(params, key) ? params[key] : null;
}

export const locations = {
    SIMILAR_SOLD: 'SIMILAR_SOLD',
    STICKY_HEADER: 'STICKY_HEADER',
} as const;

export type Location = (typeof locations)[keyof typeof locations];

const purchaseLocation: { [K in Location]: string } = {
    [locations.SIMILAR_SOLD]: 'similar sold item purchase button',
    [locations.STICKY_HEADER]: 'sticky header',
};

const makeAnOfferLocation: { [K in Location]: string } = {
    [locations.SIMILAR_SOLD]: 'similar sold item make offer button',
    [locations.STICKY_HEADER]: 'sticky header',
};

function getPurchaseStatus(item?: Maybe<PdpTrackingVpvProps>): Maybe<string> {
    return getPdpTrackingVpvParamValue(item?.pdpTrackingVpvParams, 'purchaseStatus');
}

const itemFragment = graphql`
    fragment actionButtonsTracking_item on Item @inline {
        serviceId
        contemporaryTrackingString
        title
        seller {
            serviceId
        }
        pricing {
            totalAmount {
                convertedAmounts {
                    USD
                    GBP
                    EUR
                    CAD
                    AUD
                    CHF
                    MXN
                    NOK
                    SEK
                    DKK
                }
            }
        }
        categoryCode
        browseUrl
        firstPhotoWebPath(size: small)
        localizedPdpUrl
    }
`;

export function fireTrackEcommerce({
    item: itemRef = null,
    eventName = undefined,
    environment,
    quantity,
}: {
    item: Maybe<Item>;
    eventName?: EventNameValues;
    environment: Environment;
    quantity?: number | null;
}): void {
    const item = readInlineData(itemFragment, itemRef);
    const locale = serverVars.get('locale');
    const name = locale === 'en-US' ? item?.title : 'n/a';
    const {
        seller,
        pricing,
        browseUrl,
        serviceId,
        categoryCode,
        contemporaryTrackingString,
        firstPhotoWebPath,
        localizedPdpUrl,
    } = item || {};

    const price = pricing?.totalAmount?.convertedAmounts?.USD;
    const dimension76 = getPdpTrackingVpvParamValue(itemRef?.pdpTrackingVpvParams, 'priceType');

    trackEcommerce({
        type: 'addToCart',
        eventName,
        products: [
            {
                brand: seller?.serviceId,
                category: browseUrl,
                categoryCode: categoryCode,
                dimension76,
                dimension83: contemporaryTrackingString,
                id: serviceId,
                name,
                price,
                convertedAmounts: pricing?.totalAmount?.convertedAmounts,
                quantity,
                ...(firstPhotoWebPath ? { image: firstPhotoWebPath } : {}),
                ...(localizedPdpUrl ? { url: localizedPdpUrl } : {}),
            },
        ],
    });

    const userEventItem = getUserEventItem({
        id: serviceId,
        amount: price,
        quantity,
        convertedAmounts: pricing?.totalAmount?.convertedAmounts,
    });
    if (userEventItem) {
        trackUserEventAddToCart(environment, [userEventItem]);
    }
}

export function trackMakeOfferFromEmail(template: string, item?: Maybe<PdpTrackingVpvProps>): void {
    const trigger = `email make offer button - ${(template || '').toLowerCase()}`;
    trackEvent({
        category: 'make offer click',
        action: trigger,
        eventName: EVENT_PRODUCT_INTERACTION,
        label: getPurchaseStatus(item),
        interaction_type: INTERACTION_TYPE_MAKE_OFFER,
        step_interaction_name: STEP_INTERACTION_OFFER_INQUIRY_ENTRY,
        isInteractiveEvent: true,
        trigger,
    });
}

export function trackMakeOfferClick({
    item,
    hasPrivateOffer = false,
    event = null,
    location,
    environment,
    quantity,
}: {
    item: Maybe<Item>;
    hasPrivateOffer: Maybe<boolean>;
    event: TrackableEvent;
    location?: Location;
    environment: Environment;
    quantity?: number | null;
}): void {
    let trackObj;

    if (hasPrivateOffer && !location) {
        trackObj = {
            action: 'offer pdp counter offer',
            category: 'counter offer click',
            label: getPurchaseStatus(item),
            eventName: EVENT_PRODUCT_INTERACTION,
            interaction_type: INTERACTION_TYPE_COUNTER_OFFER,
            step_interaction_name: STEP_INTERACTION_COUNTER_OFFER,
            trigger: 'pdp main',
        };
    } else {
        const trigger = location && makeAnOfferLocation[location];

        trackObj = {
            category: 'make offer click',
            action: trigger || 'pdp make offer button',

            eventName: EVENT_PRODUCT_INTERACTION,
            label: trigger || getPurchaseStatus(item),
            interaction_type: INTERACTION_TYPE_MAKE_OFFER,
            step_interaction_name: STEP_INTERACTION_MAKE_OFFER,
            additional: {
                currentOfferType: `${hasPrivateOffer ? 'private' : 'regular'} offer`,
            },
            trigger: trigger || 'pdp main',
        };
    }

    trackEvent(trackObj, event);
    fireTrackEcommerce({ item, eventName: EVENT_ADD_CART, environment, quantity });
}

export function trackPurchaseClick({
    item = null,
    event = null,
    location,
    hasPrivateOffer = false,
    environment,
    quantity,
}: {
    item: Maybe<Item>;
    event: TrackableEvent;
    location?: Location;
    hasPrivateOffer?: Maybe<boolean>;
    environment: Environment;
    quantity?: number | null;
}): void {
    if (location && !!purchaseLocation[location]) {
        trackEvent(
            {
                category: 'purchase click',
                action: purchaseLocation[location],
                eventName: EVENT_PRODUCT_INTERACTION,
                label: purchaseLocation[location],
                interaction_type: INTERACTION_TYPE_ADD_TO_CART,
                step_interaction_name: STEP_INTERACTION_ADD_TO_CART,
                trigger: purchaseLocation[location],
            },
            event
        );
    } else if (hasPrivateOffer) {
        trackEvent(
            {
                category: 'accept offer click',
                action: 'offer pdp accept offer',
                label: getPurchaseStatus(item),
                eventName: EVENT_PRODUCT_INTERACTION,
                interaction_type: INTERACTION_TYPE_ACCEPT_OFFER,
                step_interaction_name: STEP_INTERACTION_ACCEPT_OFFER,
                trigger: 'pdp main',
            },
            event
        );
    } else {
        trackEvent(
            {
                category: 'purchase click',
                action: 'pdp purchase button',
                eventName: EVENT_PRODUCT_INTERACTION,
                label: getPurchaseStatus(item),
                interaction_type: INTERACTION_TYPE_ADD_TO_CART,
                step_interaction_name: STEP_INTERACTION_ADD_TO_CART,
                trigger: 'pdp main',
            },
            event
        );
    }
    fireTrackEcommerce({ item, eventName: EVENT_ADD_CART, environment, quantity });
}
